/**
 * Animal group operations service.
 * Provides async functions for animal group CRUD operations.
 */

import { queryClient } from "common/queryClient";
import { queryKeys } from "common/queryKeys";
import { fetchApi } from "common/utils/Api";
import {
  prepareAnimalGroupRequest,
  processAnimalGroupResponse,
} from "common/utils/helpers";
import { AnimalGroup } from "model";

/**
 * Create an animal group.
 * After creation, invalidates the farm query to ensure components
 * that depend on farm.animalGroups re-render with the new animal group.
 */
export async function createAnimalGroup(
  animalGroup: Partial<AnimalGroup>
): Promise<AnimalGroup> {
  // Convert postGrazingTarget from kg/ha (client) to kg/m² (server)
  const requestData = prepareAnimalGroupRequest(animalGroup as AnimalGroup);
  const result = (await fetchApi("animalGroups", {
    method: "POST",
    body: JSON.stringify(requestData),
  })) as AnimalGroup;

  // Fetch the full animal group data
  const fullAnimalGroup = (await fetchApi(
    `animalGroups/${result.guid}`
  )) as AnimalGroup;
  const processedAnimalGroup = processAnimalGroupResponse([fullAnimalGroup])[0];

  // Add to React Query cache
  queryClient.setQueryData(
    queryKeys.animalGroups.detail(processedAnimalGroup.guid),
    processedAnimalGroup
  );

  // Invalidate animal groups list
  await queryClient.invalidateQueries({
    queryKey: queryKeys.animalGroups.all,
  });

  // Invalidate farm query to ensure components that depend on farm.animalGroups re-render
  if (animalGroup.farm) {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.farms.detail(animalGroup.farm),
    });
  }

  return processedAnimalGroup;
}

/**
 * Update an animal group.
 */
export async function updateAnimalGroup(
  animalGroup: AnimalGroup
): Promise<AnimalGroup> {
  // Convert postGrazingTarget from kg/ha (client) to kg/m² (server)
  const requestData = prepareAnimalGroupRequest(animalGroup);
  await fetchApi(`animalGroups/${animalGroup.guid}`, {
    method: "PUT",
    body: JSON.stringify(requestData),
  });

  // Fetch the updated animal group data
  const updatedAnimalGroup = (await fetchApi(
    `animalGroups/${animalGroup.guid}`
  )) as AnimalGroup;
  const processedAnimalGroup = processAnimalGroupResponse([
    updatedAnimalGroup,
  ])[0];

  // Update React Query cache
  queryClient.setQueryData(
    queryKeys.animalGroups.detail(processedAnimalGroup.guid),
    processedAnimalGroup
  );

  // Invalidate animal groups list
  await queryClient.invalidateQueries({
    queryKey: queryKeys.animalGroups.all,
  });

  return processedAnimalGroup;
}

/**
 * Delete an animal group.
 */
export async function deleteAnimalGroup(animalGroupId: string): Promise<void> {
  await fetchApi(`animalGroups/${animalGroupId}`, {
    method: "DELETE",
  });
}

/**
 * Fetch animal groups by IDs.
 */
export async function fetchAnimalGroupsById(
  animalGroupIds: string[]
): Promise<AnimalGroup[]> {
  if (!animalGroupIds || animalGroupIds.length === 0) {
    return [];
  }
  return fetchApi(`animalGroups?ids=${animalGroupIds.join(",")}`) as Promise<
    AnimalGroup[]
  >;
}
